// A club as one of its athletes sees it (see `features/athlete-clubs.md`) —
// everything the club's public home page carries, plus what only a member sees:
// the whole roster including athletes whose own profile is private, their own
// training here, and the fortnight ahead.
//
// The server has already decided all of that (`toAthleteClubView`); this renders
// it. Deliberately laid out in the order a member cares about: what's next, then
// my training, then the club itself, then the people.

import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SportsIcon from '@mui/icons-material/Sports';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import type { AthleteClubView } from '../../domain';
import { groupSchedulePatterns, schedulePatternLabel } from '../../domain';
import { useTranslation } from '../../state/I18nContext';
import { Markdown } from '../Markdown';
import { PhotoGallery } from '../PhotoGallery';
import { ClubMatesList } from './ClubMatesList';
import { UpcomingSessionRow } from './UpcomingSessionRow';
import { publicClubUrl } from '../public/clubSharePath';
import { publicProfileUrl } from '../public/profileSharePath';
import { publicViewUrl } from '../public/sharePath';

interface MyClubViewProps {
  club: AthleteClubView;
  onBack: () => void;
}

export function MyClubView({ club, onBack }: MyClubViewProps) {
  const { t, localeCode } = useTranslation();

  const dateFormat = useMemo(
    () => new Intl.DateTimeFormat(localeCode, { dateStyle: 'medium', timeZone: 'UTC' }),
    [localeCode],
  );

  const myClasses = club.training.filter((entry) => entry.enrolled || entry.coaching);

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
        {t('athlete.club.back')}
      </Button>

      {/* Club header. The name gets the full width on a phone — a truncated club
          name is worse than a wrapped one — with the public-page link below it. */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        sx={{ mb: 3 }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ minWidth: 0, flexGrow: 1, width: '100%' }}
        >
          <Avatar
            src={club.logoUrl ?? undefined}
            alt={club.name}
            variant="rounded"
            sx={{ width: 64, height: 64, flexShrink: 0 }}
          >
            <ShieldOutlinedIcon />
          </Avatar>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="h5">{club.name}</Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              {club.isHome && (
                <Chip size="small" color="primary" label={t('athlete.clubs.homeClub')} />
              )}
              {club.locationName && (
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<PlaceOutlinedIcon />}
                  label={club.locationName}
                />
              )}
            </Stack>
          </Box>
        </Stack>
        <Button
          size="small"
          endIcon={<OpenInNewIcon />}
          sx={{ flexShrink: 0 }}
          component="a"
          href={publicClubUrl(club.homeToken, window.location.origin)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('athlete.club.publicPage')}
        </Button>
      </Stack>

      <Stack spacing={3}>
        {/* What's next here — the reason an athlete opens this page mid-week. */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('athlete.club.upcoming')}
          </Typography>
          {club.upcoming.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('athlete.club.noUpcoming')}
            </Typography>
          ) : (
            <Stack divider={<Divider flexItem />}>
              {club.upcoming.map((session, i) => (
                <Box key={`${session.trainingClassId}-${session.date}-${session.startTime}`} sx={{ py: i === 0 ? 0 : 1 }}>
                  <UpcomingSessionRow session={session} highlight={i === 0} />
                </Box>
              ))}
            </Stack>
          )}
        </Paper>

        {/* My training frequency at this club. */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('athlete.club.myTraining')}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
            <Typography variant="h4" color="primary">
              {club.attendance.perWeek}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('athlete.club.perWeekLabel', { days: club.attendance.windowDays })}
            </Typography>
          </Stack>
          {club.attendance.marked > 0 ? (
            <>
              <LinearProgress
                variant="determinate"
                value={club.attendance.rate * 100}
                sx={{ mb: 1, borderRadius: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                {t('athlete.clubs.attendance', {
                  present: club.attendance.present,
                  marked: club.attendance.marked,
                  percent: Math.round(club.attendance.rate * 100),
                })}
              </Typography>
              {club.attendance.lastAttended && (
                <Typography variant="caption" color="text.secondary">
                  {t('athlete.clubs.lastAttended', {
                    date: dateFormat.format(
                      new Date(`${club.attendance.lastAttended}T00:00:00Z`),
                    ),
                  })}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('athlete.clubs.noAttendance')}
            </Typography>
          )}

          {myClasses.length > 0 && (
            <Stack spacing={1} sx={{ mt: 2 }}>
              {myClasses.map((entry) => (
                <Stack
                  key={entry.trainingClassId}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <FitnessCenterIcon color="disabled" fontSize="small" />
                  <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                    {entry.name}
                  </Typography>
                  {entry.coaching && (
                    <Chip size="small" color="secondary" variant="outlined" label={t('athlete.club.coaching')} />
                  )}
                </Stack>
              ))}
            </Stack>
          )}
        </Paper>

        {/* The club's own description — the same markdown its public page shows. */}
        {club.description.trim().length > 0 && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {t('athlete.club.about')}
            </Typography>
            <Markdown>{club.description}</Markdown>
          </Paper>
        )}

        {/* Every training class the club runs, not only the athlete's own. */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('athlete.club.allTraining')}
          </Typography>
          {club.training.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('athlete.club.noTraining')}
            </Typography>
          ) : (
            <Stack spacing={2}>
              {club.training.map((entry) => (
                <Box key={entry.trainingClassId}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle1" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                      {entry.name}
                    </Typography>
                    {entry.audience !== 'ALL' && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color={entry.audience === 'KIDS' ? 'warning' : 'info'}
                        label={t(`admin.trainingClasses.audience.${entry.audience}`)}
                      />
                    )}
                    {entry.enrolled && (
                      <Chip size="small" color="primary" label={t('athlete.club.enrolled')} />
                    )}
                    {entry.coaching && (
                      <Chip size="small" color="secondary" label={t('athlete.club.coaching')} />
                    )}
                  </Stack>
                  {entry.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {entry.description}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    {groupSchedulePatterns(entry.schedule).map((pattern, i) => (
                      <Chip
                        key={i}
                        size="small"
                        variant="outlined"
                        label={schedulePatternLabel(pattern, (day) =>
                          t(`admin.trainingClasses.weekdays.${day}`),
                        )}
                      />
                    ))}
                    {entry.coaches.map((coach, i) => (
                      <Chip
                        key={`c${i}`}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        icon={<SportsIcon />}
                        label={coach.displayName}
                      />
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Paper>

        {/* Coaching staff — every coach, including ones the public page hides. */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('athlete.club.coaches')}
          </Typography>
          {club.coaches.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('athlete.club.noCoaches')}
            </Typography>
          ) : (
            <Stack spacing={2}>
              {club.coaches.map((coach) => (
                <Stack key={coach.profileToken} direction="row" spacing={2} alignItems="center">
                  <Avatar src={coach.photoUrl ?? undefined} alt={coach.displayName}>
                    <SportsIcon />
                  </Avatar>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography variant="subtitle2" noWrap>
                      {coach.displayName}
                    </Typography>
                    {coach.description && (
                      <Typography variant="body2" color="text.secondary">
                        {coach.description}
                      </Typography>
                    )}
                  </Box>
                  {/* Only offered when the coach's page is actually public — the
                      server's own gate, mirrored so no link 404s. */}
                  {coach.isPublic && (
                    <Button
                      size="small"
                      endIcon={<OpenInNewIcon />}
                      component="a"
                      href={publicProfileUrl(coach.profileToken, window.location.origin)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('athlete.club.openProfile')}
                    </Button>
                  )}
                </Stack>
              ))}
            </Stack>
          )}
        </Paper>

        {/* The club's photos, exactly as the public page shows them. */}
        {club.photos.length > 0 && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('athlete.club.photos')}
            </Typography>
            <PhotoGallery
              urls={club.photos.map((photo) => photo.url)}
              alt={club.name}
              emptyLabel={t('athlete.club.noPhotos')}
              labels={{
                close: t('gallery.close'),
                download: t('gallery.download'),
                previous: t('gallery.previous'),
                next: t('gallery.next'),
              }}
            />
          </Paper>
        )}

        {/* Tournaments the club organises — present only when the club opts in,
            the same rule its public page follows. */}
        {club.tournaments.length > 0 && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('athlete.club.tournaments')}
            </Typography>
            <Stack spacing={1.5}>
              {club.tournaments.map((tournament) => (
                <Stack key={tournament.id} direction="row" spacing={1.5} alignItems="center">
                  <EmojiEventsIcon color="disabled" />
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography variant="subtitle2" noWrap>
                      {tournament.name}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t(`admin.common.status.${tournament.status}`)}
                      />
                      {tournament.registrationOpen && (
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          label={t('athlete.club.registrationOpen')}
                        />
                      )}
                    </Stack>
                  </Box>
                  <Button
                    size="small"
                    endIcon={<OpenInNewIcon />}
                    component="a"
                    href={publicViewUrl(tournament.id, window.location.origin)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('athlete.club.openTournament')}
                  </Button>
                </Stack>
              ))}
            </Stack>
          </Paper>
        )}

        {/* The people. Every member, whatever their own profile visibility says. */}
        <ClubMatesList clubMates={club.roster} clubName={club.name} />
      </Stack>
    </Box>
  );
}
